// frontend/src/pages/JefeDashboard/JefeDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './JefeDashboard.css';
import { FaCheck, FaTimes, FaUserCheck, FaHourglassHalf, FaClipboardCheck, FaBan, FaInbox, FaProjectDiagram, FaCalendar, FaChartLine, FaUsers, FaTasks, FaClock, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { fetchAuth } from '../../utils/api';

// Tareas ahora provienen del backend; sin mock

const StatusTag = ({ estado }) => {
  let className = 'status-tag';
  let icon = null;
  switch (estado) {
    case 'EN_REVISION': className += ' en-revision'; icon = <FaHourglassHalf />; break;
    case 'APROBADO': className += ' aprobado'; icon = <FaCheck />; break;
    case 'RECHAZADO': className += ' rechazado'; icon = <FaBan />; break;
    default: break;
  }
  return <span className={className}>{icon} {(estado || '').replace('_', ' ')}</span>;
};

export default function JefeDashboard() {
  // Estado de proyectos y tareas
  const [proyectos, setProyectos] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const diasHasta = (s) => {
    if (!s) return null;
    try {
      const d = new Date(s);
      const now = new Date();
      const diff = (d.getTime() - now.getTime()) / 86400000;
      return Math.ceil(diff);
    } catch { return null; }
  };

  const toUIEstado = (estado) => {
    switch (estado) {
      case 'PENDIENTE': return 'Pendiente';
      case 'ACTIVO': return 'En Progreso';
      case 'RECHAZADO': return 'Cancelado';
      default: return estado || 'Pendiente';
    }
  };

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Obtener proyectos asignados al jefe desde el backend
        const data = await fetchAuth('/api/jefe/proyectos/');
        
        // Los proyectos ya vienen formateados del backend
        const proyectosFormateados = (data.proyectos || []).map(proyecto => ({
          id_proyecto: proyecto.id_proyecto,
          nombre_proyecto: proyecto.nombre_proyecto,
          descripcion: proyecto.descripcion,
          fecha_inicio: proyecto.fecha_inicio,
          fecha_fin: proyecto.fecha_fin,
          estado: toUIEstado(proyecto.estado),
          colaboradores: proyecto.colaboradores || [],
          tareas_completadas: proyecto.tareas_completadas || 0,
          tareas_totales: proyecto.tareas_totales || 0,
          progreso: typeof proyecto.progreso === 'number' ? proyecto.progreso : null,
          etapa_actual: proyecto.etapa_actual || null
        }));
        
        setProyectos(proyectosFormateados);
        
        // Cargar tareas reales del backend
        try {
          const tareasData = await fetchAuth('/api/jefe/tareas/');
          setTasks(tareasData.tareas || []);
        } catch (e) {
          console.error('Error cargando tareas del jefe:', e);
          setTasks([]);
        }
        
      } catch (err) {
        console.error('Error al cargar datos del jefe:', err);
        setError(err.message || 'Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    
    cargarDatos();
  }, []);

  const stats = {
    total: tasks.length,
    enRevision: tasks.filter(t => t.estado === 'EN_REVISION').length,
    aprobadas: tasks.filter(t => t.estado === 'APROBADO').length,
    rechazadas: tasks.filter(t => t.estado === 'RECHAZADO').length,
    invitacionesPendientes: proyectos.filter(p => p.estado === 'Pendiente').length,
    proyectosActivos: proyectos.filter(p => p.estado === 'En Progreso').length,
    totalProyectos: proyectos.length,
    totalColaboradores: new Set(proyectos.flatMap(p => p.colaboradores || [])).size,
  };

  const proximasEntregas = tasks
    .filter(t => !!t.fecha_entrega)
    .map(t => ({ 
      id: t.id, 
      nombre: t.nombre, 
      proyecto: t.proyecto?.nombre || 'Sin proyecto',
      dias: diasHasta(t.fecha_entrega),
      fecha: t.fecha_entrega
    }))
    .filter(x => x.dias !== null && x.dias >= 0 && x.dias <= 7)
    .sort((a,b) => (a.dias||99) - (b.dias||99))
    .slice(0, 5);

  // Proyectos con mayor progreso
  const proyectosConProgreso = proyectos
    .filter(p => p.estado === 'En Progreso')
    .map(p => ({
      ...p,
      progreso: (p.progreso != null) 
        ? p.progreso 
        : (p.tareas_totales > 0 ? Math.round((p.tareas_completadas / p.tareas_totales) * 100) : 0)
    }))
    .sort((a, b) => b.progreso - a.progreso)
    .slice(0, 5);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-4"></div>
          <p className="text-slate-600 text-lg font-medium">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h3 className="text-red-800 text-xl font-bold mb-2">Error al cargar</h3>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-slate-100 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-4 rounded-2xl shadow-lg">
              <FaChartLine className="text-white text-3xl" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-slate-800 tracking-tight">
                Dashboard de Jefe
              </h1>
              <p className="text-slate-600 mt-1">
                Resumen ejecutivo de tus proyectos y equipos
              </p>
            </div>
          </div>
        </div>

        {/* Notificaciones */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200 mb-6">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <FaInbox className="text-indigo-600" />
            Notificaciones Importantes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Invitaciones */}
            <div 
              onClick={() => navigate('/app/jefe/invitaciones')}
              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200 hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="bg-blue-500 p-2.5 rounded-lg group-hover:scale-110 transition-transform">
                  <FaInbox className="text-white text-xl" />
                </div>
                <span className="bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                  {stats.invitacionesPendientes}
                </span>
              </div>
              <h4 className="text-slate-800 font-semibold text-sm mb-1">Invitaciones</h4>
              <p className="text-slate-600 text-xs">
                {stats.invitacionesPendientes > 0 
                  ? 'Tienes invitaciones pendientes' 
                  : 'Sin invitaciones pendientes'}
              </p>
            </div>

            {/* Tareas en Revisión */}
            <div 
              onClick={() => navigate('/app/jefe/tareas')}
              className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200 hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="bg-amber-500 p-2.5 rounded-lg group-hover:scale-110 transition-transform">
                  <FaHourglassHalf className="text-white text-xl" />
                </div>
                <span className="bg-amber-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                  {stats.enRevision}
                </span>
              </div>
              <h4 className="text-slate-800 font-semibold text-sm mb-1">En Revisión</h4>
              <p className="text-slate-600 text-xs">
                {stats.enRevision > 0 
                  ? 'Hay tareas esperando tu revisión' 
                  : 'Sin tareas en revisión'}
              </p>
            </div>

            {/* Entregas Próximas */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200 hover:shadow-md transition-all cursor-pointer group">
              <div className="flex items-start justify-between mb-2">
                <div className="bg-purple-500 p-2.5 rounded-lg group-hover:scale-110 transition-transform">
                  <FaCalendar className="text-white text-xl" />
                </div>
                <span className="bg-purple-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                  {proximasEntregas.length}
                </span>
              </div>
              <h4 className="text-slate-800 font-semibold text-sm mb-1">Entregas Próximas</h4>
              <p className="text-slate-600 text-xs">
                {proximasEntregas.length > 0 
                  ? `En los próximos 7 días` 
                  : 'Sin entregas próximas'}
              </p>
            </div>
          </div>
        </div>

        {/* Estadísticas Generales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-3 rounded-xl">
                <FaProjectDiagram className="text-white text-2xl" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-slate-800">{stats.proyectosActivos}</h3>
            <p className="text-slate-600 text-sm mt-1">Proyectos Activos</p>
            <p className="text-xs text-slate-400 mt-2">de {stats.totalProyectos} totales</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-3 rounded-xl">
                <FaTasks className="text-white text-2xl" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-slate-800">{stats.total}</h3>
            <p className="text-slate-600 text-sm mt-1">Tareas Totales</p>
            <p className="text-xs text-slate-400 mt-2">{stats.aprobadas} aprobadas</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl">
                <FaUsers className="text-white text-2xl" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-slate-800">{stats.totalColaboradores}</h3>
            <p className="text-slate-600 text-sm mt-1">Colaboradores</p>
            <p className="text-xs text-slate-400 mt-2">en tus proyectos</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-3 rounded-xl">
                <FaClock className="text-white text-2xl" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-slate-800">{stats.enRevision}</h3>
            <p className="text-slate-600 text-sm mt-1">Por Revisar</p>
            <p className="text-xs text-slate-400 mt-2">tareas pendientes</p>
          </div>
        </div>
      </div>

      {/* Contenido Principal */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Progreso de Proyectos */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <FaProjectDiagram className="text-indigo-600" />
            Progreso de Proyectos Activos
          </h3>
          {proyectosConProgreso.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FaProjectDiagram className="text-4xl mx-auto mb-3 opacity-30" />
              <p>No hay proyectos activos</p>
            </div>
          ) : (
            <div className="space-y-4">
              {proyectosConProgreso.map(proyecto => (
                <div 
                  key={proyecto.id_proyecto}
                  onClick={() => navigate(`/app/jefe/proyectos/${proyecto.id_proyecto}`)}
                  className="border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-slate-800 text-sm">{proyecto.nombre_proyecto}</h4>
                    {proyecto.etapa_actual && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border">{proyecto.etapa_actual}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{proyecto.tareas_completadas} / {proyecto.tareas_totales} tareas</span>
                    <span className="flex items-center gap-1">
                      <FaUsers className="text-xs" />
                      {proyecto.colaboradores?.length || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Próximas Entregas */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <FaCalendar className="text-purple-600" />
            Próximas Entregas (7 días)
          </h3>
          {proximasEntregas.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FaCheckCircle className="text-4xl mx-auto mb-3 opacity-30" />
              <p>No hay entregas próximas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {proximasEntregas.map(entrega => (
                <div 
                  key={entrega.id}
                  className="border border-slate-200 rounded-xl p-4 hover:border-purple-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-800 text-sm mb-1">{entrega.nombre}</h4>
                      <p className="text-xs text-slate-500">{typeof entrega.proyecto === 'object' ? (entrega.proyecto.nombre || '—') : (entrega.proyecto || '—')}</p>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                      entrega.dias === 0 ? 'bg-red-100 text-red-700' :
                      entrega.dias <= 2 ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {entrega.dias === 0 ? (
                        <>
                          <FaExclamationTriangle className="text-xs" />
                          Hoy
                        </>
                      ) : entrega.dias === 1 ? (
                        <>
                          <FaClock className="text-xs" />
                          Mañana
                        </>
                      ) : (
                        <>
                          <FaClock className="text-xs" />
                          {entrega.dias} días
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {formatDate(entrega.fecha)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Accesos Rápidos */}
      <div className="max-w-7xl mx-auto mt-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-4">Accesos Rápidos</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/app/jefe/proyectos')}
              className="bg-gradient-to-br from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 border border-indigo-200 rounded-xl p-4 text-left transition-all hover:shadow-md group"
            >
              <FaProjectDiagram className="text-indigo-600 text-2xl mb-2 group-hover:scale-110 transition-transform" />
              <h4 className="font-semibold text-slate-800 text-sm">Mis Proyectos</h4>
              <p className="text-xs text-slate-600 mt-1">{stats.proyectosActivos} activos</p>
            </button>

            <button
              onClick={() => navigate('/app/jefe/tareas')}
              className="bg-gradient-to-br from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 border border-amber-200 rounded-xl p-4 text-left transition-all hover:shadow-md group"
            >
              <FaClipboardCheck className="text-amber-600 text-2xl mb-2 group-hover:scale-110 transition-transform" />
              <h4 className="font-semibold text-slate-800 text-sm">Revisar Tareas</h4>
              <p className="text-xs text-slate-600 mt-1">{stats.enRevision} pendientes</p>
            </button>

            <button
              onClick={() => navigate('/app/jefe/invitaciones')}
              className="bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded-xl p-4 text-left transition-all hover:shadow-md group"
            >
              <FaInbox className="text-blue-600 text-2xl mb-2 group-hover:scale-110 transition-transform" />
              <h4 className="font-semibold text-slate-800 text-sm">Invitaciones</h4>
              <p className="text-xs text-slate-600 mt-1">{stats.invitacionesPendientes} nuevas</p>
            </button>

            <button
              onClick={() => navigate('/app/perfil')}
              className="bg-gradient-to-br from-emerald-50 to-emerald-100 hover:from-emerald-100 hover:to-emerald-200 border border-emerald-200 rounded-xl p-4 text-left transition-all hover:shadow-md group"
            >
              <FaUserCheck className="text-emerald-600 text-2xl mb-2 group-hover:scale-110 transition-transform" />
              <h4 className="font-semibold text-slate-800 text-sm">Mi Perfil</h4>
              <p className="text-xs text-slate-600 mt-1">Configuración</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
  const formatDate = (s) => {
    const a = String(s || '')
    const part = a.split('T')[0].split(' ')[0]
    if (!part) return '-'
    const [y, m, d] = part.split('-')
    return `${d}/${m}/${y}`
  }